import React, { useState } from "react"
import PropTypes from "prop-types"
import helper from "helpers/fn_helper"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Spinner,
} from "reactstrap"

const ImageUploadModal = ({
  isOpen,
  toggle,
  onSave,
  currentImage,
  currentTitle = "",
  currentAlt = "",
}) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(currentImage || null)
  const [titleAttribute, setTitleAttribute] = useState(currentTitle)
  const [altAttribute, setAltAttribute] = useState(currentAlt)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedImageUrl, setUploadedImageUrl] = useState(currentImage || null)

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    processFile(file)
  }

  const processFile = (file) => {
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg']
      if (!validTypes.includes(file.type)) {
        alert('File type must be png, jpg, or jpeg only')
        return
      }

      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024 // 10MB in bytes
      if (file.size > maxSize) {
        alert('File size must not exceed 10 MB')
        return
      }

      setSelectedFile(file)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    
    const file = e.dataTransfer.files[0]
    processFile(file)
  }

  const handleSave = async () => {
    if (!selectedFile) {
      alert('Please select an image first')
      return
    }

    try {
      setIsUploading(true)
      
      // Create FormData for file upload
      const formData = new FormData()
      formData.append('file', selectedFile)

      console.log('Uploading file:', selectedFile)
      console.log('FormData has file:', formData.has('file'))

      // Use fetch API instead of axios for better multipart/form-data support
      const response = await fetch(
        process.env.REACT_APP_BACKEND_URL + "/upload?folder=line-campaign",
        {
          method: 'POST',
          body: formData,
          headers: {
            authorization: helper.Auth.getToken(),
          },
          credentials: 'include', // for cookies
          // Don't set Content-Type header, let browser set it with boundary
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Upload error:', errorText)
        throw new Error(`Upload failed: ${response.status}`)
      }

      const result = await response.json()
      console.log('Upload result:', result)

      if (result && result.data && result.data.url) {
        const imageUrl = result.data.url
        
        // Save with uploaded URL
        onSave({
          file: selectedFile,
          preview: imageUrl,
          url: imageUrl,
          titleAttribute,
          altAttribute,
        })
        
        setUploadedImageUrl(imageUrl)
        handleClose()
      } else {
        alert('Failed to upload image. Please try again.')
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      alert('Error uploading image. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setImagePreview(currentImage || null)
    setTitleAttribute(currentTitle)
    setAltAttribute(currentAlt)
    setUploadedImageUrl(currentImage || null)
    setIsUploading(false)
    toggle()
  }

  // Update state when props change
  React.useEffect(() => {
    if (isOpen) {
      setImagePreview(currentImage || null)
      setTitleAttribute(currentTitle)
      setAltAttribute(currentAlt)
      setUploadedImageUrl(currentImage || null)
    }
  }, [isOpen, currentImage, currentTitle, currentAlt])

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
      <ModalHeader toggle={handleClose}>
        Browse File
      </ModalHeader>
      <ModalBody>
        {/* File Upload Area */}
        <FormGroup>
          <Label>
            Browse File <span className="text-danger">*</span>
          </Label>
          <p className="text-muted mb-2" style={{ fontSize: "0.875rem" }}>
            File type png, jpg, jpeg only, Max filesize 10 MB
          </p>
          
          {/* Drag and Drop Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
            style={{
              border: `2px dashed ${isDragging ? "#34c38f" : "#d0d0d0"}`,
              borderRadius: "8px",
              padding: "40px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragging ? "#f0f8f5" : "#f8f9fa",
              transition: "all 0.3s ease",
              marginBottom: "16px"
            }}
          >
            {imagePreview ? (
              <div>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    borderRadius: "8px",
                    marginBottom: "16px"
                  }}
                />
                <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
                  Click or drag to change image
                </p>
              </div>
            ) : (
              <div>
                <i className="bx bx-cloud-upload" style={{ fontSize: "48px", color: "#6c757d", marginBottom: "16px" }}></i>
                <p className="mb-2" style={{ fontSize: "1rem", fontWeight: "500" }}>
                  Click to upload
                </p>
                <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
                  or drag and drop
                </p>
                <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
                  SVG, PNG, JPG or GIF (max. 10MB)
                </p>
              </div>
            )}
          </div>

          <Input
            type="file"
            id="fileInput"
            accept="image/png, image/jpeg, image/jpg"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </FormGroup>

        {/* Title Attribute */}
        <FormGroup>
          <Label htmlFor="titleAttribute">Title Attribute</Label>
          <Input
            type="text"
            id="titleAttribute"
            placeholder="Enter title attribute"
            value={titleAttribute}
            onChange={(e) => setTitleAttribute(e.target.value)}
          />
        </FormGroup>

        {/* Alt Attribute */}
        <FormGroup>
          <Label htmlFor="altAttribute">Alt Attribute</Label>
          <Input
            type="text"
            id="altAttribute"
            placeholder="Enter alt attribute"
            value={altAttribute}
            onChange={(e) => setAltAttribute(e.target.value)}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleClose} disabled={isUploading}>
          Close
        </Button>
        <Button color="success" onClick={handleSave} disabled={isUploading}>
          {isUploading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Uploading...
            </>
          ) : (
            <>
              Next <i className="bx bx-right-arrow-alt ms-1"></i>
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

ImageUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentImage: PropTypes.string,
  currentTitle: PropTypes.string,
  currentAlt: PropTypes.string,
}

export default ImageUploadModal
